import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import {
  DS2026_MODAL_BODY_CLASS,
  DS2026_MODAL_BUTTON_CLASS,
  DS2026_MODAL_CONTENT_CLASS,
  DS2026_MODAL_FOOTER_CLASS,
  DsTextField,
  FieldGroup,
} from 'components/FbUI/Ds2026Field'
// The Radix modal (IModalProps: open/onAccept/…) — NOT the default
// `components/UI/Modal` export, which is a react-bootstrap wrapper keyed on
// `show` and would silently ignore `open`. Matches ContactModal.
import { Modal } from 'components/UI/Modal/Modal'
import { EmailBodyEditor } from './SequenceTab/EmailBodyEditor'
import { useSaveReplyTemplate } from './reply_templates_api'

interface FormValues {
  name: string
  description: string
  body: string
}

/**
 * Create / "save as" modal for reply templates (FIR-7582). Uses the shared
 * DS2026 `Modal` (Radix) so it's a proper viewport-centered dialog with a
 * full-screen backdrop and scrolls on short viewports — unlike a content-scoped
 * overlay, which got clipped and only dimmed part of the screen.
 *
 * `initialBody` seeds the Message field — the composer's "Save reply as a
 * template" affordance passes the current draft so the user only adds a name.
 * The parent conditionally mounts this component, so `initialBody` is read once
 * as the form's default.
 */
export function SaveReplyTemplateModal({
  onClose,
  initialBody = '',
  onSaved,
}: {
  onClose: () => void
  initialBody?: string
  onSaved?: () => void
}) {
  const saveMutation = useSaveReplyTemplate()
  const { control, watch, getValues } = useForm<FormValues>({
    defaultValues: { name: '', description: '', body: initialBody },
    mode: 'onBlur',
  })
  const canSave = !!watch('name').trim()

  // Async so the modal stays open if the save rejects (baseAPICall toasts the
  // error); on success we close it explicitly (the Modal is controlled by
  // `open`/`onOpenChange`, so it won't close itself).
  const handleAccept = async () => {
    const { name, description, body } = getValues()
    const trimmedName = name.trim()
    if (!trimmedName) return
    await saveMutation.mutateAsync({
      name: trimmedName,
      description: description.trim(),
      body,
    })
    toast.success(`Saved "${trimmedName}"`)
    onSaved?.()
    onClose()
  }

  return (
    <Ds2026ButtonProvider>
      <Modal
        open
        onOpenChange={(next: boolean) => {
          if (!next) onClose()
        }}
        title={<span className="text-neutral-800">New reply template</span>}
        description={
          <span className="text-neutral-500">
            Save a canned response you can drop into replies. Use merge fields
            like {'{FIRST_NAME}'} or {'{COMPANY}'} to personalize.
          </span>
        }
        loading={saveMutation.isPending}
        onAccept={handleAccept}
        acceptButtonText="Save template"
        blockAccept={!canSave}
        acceptButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
        onCancel={onClose}
        cancelButtonText="Cancel"
        cancelButtonProps={{
          type: 'button',
          className: DS2026_MODAL_BUTTON_CLASS,
        }}
        dialogFooterClassName={DS2026_MODAL_FOOTER_CLASS}
        contentClassName={DS2026_MODAL_CONTENT_CLASS}
        closeIconClassName="rounded-lg hover:text-neutral-500"
      >
        <FieldGroup className={DS2026_MODAL_BODY_CLASS}>
          <DsTextField
            control={control}
            name="name"
            label="Name"
            placeholder="e.g. Pricing follow-up"
            rules={{ required: 'Give your template a name' }}
          />
          <DsTextField
            control={control}
            name="description"
            label="Description"
            optional
            placeholder="Short note about when to use this"
          />
          <EmailBodyEditor
            control={control}
            name="body"
            label="Message"
            placeholder="Write your reply template… use Variables to personalize."
            hideHelperText
          />
        </FieldGroup>
      </Modal>
    </Ds2026ButtonProvider>
  )
}
